<template>
    <div class="google-play-reviews-page">
        <!-- Left Panel: Reviews -->
        <div class="reviews-panel">
            <div class="panel-header">
                <h2 class="panel-title">
                    <span class="material-icons google-icon">star</span>
                    {{ $t("googlePlay.reviews") }}
                    <span class="count-badge">{{ total }}</span>
                </h2>
                <div class="header-actions">
                    <a-select
                        v-model="filters.appId"
                        :placeholder="$t('googlePlay.selectApp')"
                        style="width: 200px"
                        size="small"
                        allowClear
                    >
                        <a-select-option value="">{{ $t("googlePlay.allApps") }}</a-select-option>
                        <a-select-option v-for="app in apps" :key="app._id" :value="app._id">
                            {{ app.appName | truncate(40) }}
                        </a-select-option>
                    </a-select>
                    <a-select v-model="filters.status" style="width: 120px" size="small">
                        <a-select-option value="">{{ $t("googlePlay.all") }}</a-select-option>
                        <a-select-option value="new">{{ $t("googlePlay.new") }}</a-select-option>
                        <a-select-option value="replied">{{ $t("googlePlay.replied") }}</a-select-option>
                        <a-select-option value="resolved">{{ $t("googlePlay.resolved") }}</a-select-option>
                    </a-select>
                    <a-select v-model="filters.starRating" style="width: 100px" size="small">
                        <a-select-option value="">{{ $t("googlePlay.allRatings") }}</a-select-option>
                        <a-select-option :value="5">5 Stars</a-select-option>
                        <a-select-option :value="4">4 Stars</a-select-option>
                        <a-select-option :value="3">3 Stars</a-select-option>
                        <a-select-option :value="2">2 Stars</a-select-option>
                        <a-select-option :value="1">1 Star</a-select-option>
                    </a-select>
                    <a-button size="small" @click="fetchReviews" :loading="loading">
                        <a-icon type="reload" />
                    </a-button>
                </div>
            </div>

            <div class="reviews-list" v-if="!loading">
                <div
                    class="review-item"
                    v-for="review in reviews"
                    :key="review._id"
                    :class="{ selected: selectedReview && selectedReview._id === review._id }"
                    @click="selectReview(review)"
                >
                    <span class="material-icons avatar-placeholder">account_circle</span>
                    <div class="review-main">
                        <div class="review-top">
                            <span class="author-name">{{ review.authorName }}</span>
                            <span class="review-date">{{ formatDate(review.createTime) }}</span>
                            <span class="status-dot" :class="review.status" :title="getStatusLabel(review.status)"></span>
                        </div>
                        <div class="star-rating">
                            <span class="material-icons" v-for="n in review.starRating" :key="'filled-' + n">star</span>
                            <span class="material-icons empty" v-for="n in (5 - review.starRating)" :key="'empty-' + n">star_border</span>
                        </div>
                        <p class="review-text" v-if="review.comment">{{ truncateText(review.comment, 120) }}</p>
                        <p class="review-text no-comment" v-else>{{ $t("googlePlay.noComment") }}</p>
                        <div class="review-bottom">
                            <span class="app-link" v-if="review.app">
                                <span class="material-icons">android</span> {{ review.app.appName | truncate(25) }}
                            </span>
                            <span v-if="review.appVersionName" class="version-badge">
                                v{{ review.appVersionName }}
                            </span>
                        </div>
                    </div>
                    <div class="review-actions-mini">
                        <a-dropdown :trigger="['click']" @click.stop>
                            <a-button size="small" type="link"><a-icon type="more" /></a-button>
                            <template #overlay>
                                <a-menu>
                                    <a-sub-menu key="status" :title="$t('googlePlay.status')">
                                        <a-menu-item key="new" @click="updateStatus(review, 'new')">
                                            <span class="status-indicator new"></span> {{ $t("googlePlay.new") }}
                                        </a-menu-item>
                                        <a-menu-item key="replied" @click="updateStatus(review, 'replied')">
                                            <span class="status-indicator replied"></span> {{ $t("googlePlay.replied") }}
                                        </a-menu-item>
                                        <a-menu-item key="resolved" @click="updateStatus(review, 'resolved')">
                                            <span class="status-indicator resolved"></span> {{ $t("googlePlay.resolved") }}
                                        </a-menu-item>
                                    </a-sub-menu>
                                    <a-menu-divider />
                                    <a-menu-item key="reply" @click="openReplyModal(review)">
                                        <a-icon type="message" /> {{ $t("googlePlay.reply") }}
                                    </a-menu-item>
                                </a-menu>
                            </template>
                        </a-dropdown>
                    </div>
                </div>

                <div class="empty-state" v-if="reviews.length === 0">
                    <span class="material-icons">reviews</span>
                    <p>{{ $t("googlePlay.noReviewsFound") }}</p>
                </div>
            </div>

            <div class="loading-state" v-else>
                <a-spin />
            </div>

            <div class="pagination-wrapper" v-if="total > pagination.limit">
                <a-pagination
                    :current="pagination.page"
                    :total="total"
                    :page-size="pagination.limit"
                    size="small"
                    @change="onPageChange"
                    @showSizeChange="onPageSizeChange"
                    show-size-changer
                    :page-size-options="['20', '50', '100']"
                />
            </div>
        </div>

        <!-- Right Panel: Review Detail -->
        <div class="detail-panel" v-if="selectedReview">
            <div class="detail-header">
                <span class="material-icons">account_circle</span>
                <div class="author-details">
                    <span class="name">{{ selectedReview.authorName }}</span>
                    <span class="date">{{ formatDate(selectedReview.createTime) }}</span>
                </div>
                <div class="badges">
                    <span class="status-badge" :class="selectedReview.status">{{ getStatusLabel(selectedReview.status) }}</span>
                </div>
            </div>

            <div class="star-rating-large">
                <span class="material-icons" v-for="n in selectedReview.starRating" :key="'filled-' + n">star</span>
                <span class="material-icons empty" v-for="n in (5 - selectedReview.starRating)" :key="'empty-' + n">star_border</span>
            </div>

            <div class="detail-body" v-if="selectedReview.comment">{{ selectedReview.comment }}</div>
            <div class="detail-body no-comment" v-else>{{ $t("googlePlay.noComment") }}</div>

            <!-- Device Info -->
            <div class="device-info" v-if="selectedReview.device || selectedReview.androidOsVersion">
                <div class="section-title">{{ $t("googlePlay.deviceInfo") }}</div>
                <div class="info-grid">
                    <div class="info-item" v-if="selectedReview.device">
                        <span class="label">{{ $t("googlePlay.device") }}:</span>
                        <span class="value">{{ selectedReview.device }}</span>
                    </div>
                    <div class="info-item" v-if="selectedReview.androidOsVersion">
                        <span class="label">Android:</span>
                        <span class="value">API {{ selectedReview.androidOsVersion }}</span>
                    </div>
                    <div class="info-item" v-if="selectedReview.appVersionName">
                        <span class="label">{{ $t("googlePlay.appVersion") }}:</span>
                        <span class="value">{{ selectedReview.appVersionName }}</span>
                    </div>
                    <div class="info-item" v-if="selectedReview.deviceMetadata && selectedReview.deviceMetadata.manufacturer">
                        <span class="label">{{ $t("googlePlay.manufacturer") }}:</span>
                        <span class="value">{{ selectedReview.deviceMetadata.manufacturer }}</span>
                    </div>
                </div>
            </div>

            <div class="reply-section" v-if="selectedReview.reply && selectedReview.reply.text">
                <div class="reply-header">
                    <span class="material-icons">reply</span>
                    {{ $t("googlePlay.yourReply") }}
                </div>
                <div class="reply-content">{{ selectedReview.reply.text }}</div>
                <div class="reply-date">{{ formatDate(selectedReview.reply.lastModified) }}</div>
            </div>

            <div class="detail-actions">
                <a-button size="small" type="primary" @click="openReplyModal(selectedReview)">
                    <a-icon type="message" /> {{ selectedReview.reply ? $t("googlePlay.updateReply") : $t("googlePlay.reply") }}
                </a-button>
                <a-dropdown :trigger="['click']">
                    <a-button size="small">{{ $t("googlePlay.status") }} <a-icon type="down" /></a-button>
                    <template #overlay>
                        <a-menu @click="(e) => updateStatus(selectedReview, e.key)">
                            <a-menu-item key="new">{{ $t("googlePlay.new") }}</a-menu-item>
                            <a-menu-item key="replied">{{ $t("googlePlay.replied") }}</a-menu-item>
                            <a-menu-item key="resolved">{{ $t("googlePlay.resolved") }}</a-menu-item>
                        </a-menu>
                    </template>
                </a-dropdown>
            </div>
        </div>

        <!-- Empty Detail Panel -->
        <div class="detail-panel empty" v-else>
            <span class="material-icons">rate_review</span>
            <p>{{ $t("googlePlay.selectReviewToPreview") }}</p>
        </div>

        <!-- Reply Modal -->
        <a-modal
            v-model="replyModal.visible"
            :title="$t('googlePlay.replyToReview')"
            @ok="submitReply"
            :confirm-loading="replyModal.loading"
            width="500px"
        >
            <div class="reply-preview" v-if="replyModal.review">
                <span class="material-icons">account_circle</span>
                <div>
                    <strong>{{ replyModal.review.authorName }}</strong>
                    <div class="star-rating-small">
                        <span class="material-icons" v-for="n in replyModal.review.starRating" :key="'filled-' + n">star</span>
                    </div>
                    <p v-if="replyModal.review.comment">{{ truncateText(replyModal.review.comment, 200) }}</p>
                </div>
            </div>
            <a-textarea
                v-model="replyModal.text"
                :placeholder="$t('googlePlay.writeYourReply')"
                :rows="4"
                :maxLength="350"
            />
            <div class="char-count">{{ replyModal.text.length }}/350</div>
        </a-modal>
    </div>
</template>

<script>
import { GOOGLE_PLAY_API } from "../../../../../api/google-play";
import moment from "moment";

export default {
    name: "ReviewList",
    props: {
        channel: {
            type: Object,
            required: true,
        },
    },
    filters: {
        truncate(value, length) {
            if (!value) return "";
            if (value.length <= length) return value;
            return value.substring(0, length) + "...";
        },
    },
    data() {
        return {
            reviews: [],
            apps: [],
            loading: false,
            total: 0,
            selectedReview: null,
            filters: {
                status: "",
                starRating: "",
                appId: "",
            },
            pagination: {
                page: 1,
                limit: 20,
            },
            replyModal: {
                visible: false,
                review: null,
                text: "",
                loading: false,
            },
        };
    },
    methods: {
        getStatusLabel(status) {
            var labels = {
                new: this.$t("googlePlay.new"),
                replied: this.$t("googlePlay.replied"),
                resolved: this.$t("googlePlay.resolved"),
            };
            return labels[status] || status;
        },
        async fetchApps() {
            try {
                const response = await GOOGLE_PLAY_API.listApps(this.channel._id);
                this.apps = response.data || [];
            } catch (err) {
                console.error("Error fetching apps", err);
            }
        },
        async fetchReviews() {
            this.loading = true;
            try {
                const response = await GOOGLE_PLAY_API.listReviews({
                    channelId: this.channel._id,
                    appId: this.filters.appId || undefined,
                    status: this.filters.status || undefined,
                    starRating: this.filters.starRating || undefined,
                    page: this.pagination.page,
                    limit: this.pagination.limit,
                });
                this.reviews = response.data.reviews;
                this.total = response.data.total;
            } catch (err) {
                this.$message.error(this.$t("googlePlay.errorFetchingReviews"));
            } finally {
                this.loading = false;
            }
        },
        selectReview(review) {
            this.selectedReview = review;
        },
        async updateStatus(review, status) {
            try {
                await GOOGLE_PLAY_API.updateStatus(review._id, status);
                review.status = status;
                if (this.selectedReview && this.selectedReview._id === review._id) {
                    this.selectedReview.status = status;
                }
                this.$message.success(this.$t("googlePlay.statusUpdated"));
            } catch (err) {
                this.$message.error(this.$t("googlePlay.errorUpdatingStatus"));
            }
        },
        openReplyModal(review) {
            this.replyModal.review = review;
            this.replyModal.text = review.reply ? review.reply.text : "";
            this.replyModal.visible = true;
        },
        async submitReply() {
            if (!this.replyModal.text.trim()) {
                this.$message.warning(this.$t("googlePlay.pleaseEnterReply"));
                return;
            }
            if (this.replyModal.text.length > 350) {
                this.$message.warning(this.$t("googlePlay.replyTooLong"));
                return;
            }
            this.replyModal.loading = true;
            try {
                await GOOGLE_PLAY_API.replyReview(this.replyModal.review._id, {
                    replyText: this.replyModal.text,
                    channelId: this.channel._id,
                });
                this.replyModal.review.status = "replied";
                this.replyModal.review.reply = {
                    text: this.replyModal.text,
                    lastModified: new Date(),
                };
                if (this.selectedReview && this.selectedReview._id === this.replyModal.review._id) {
                    this.selectedReview.status = "replied";
                    this.selectedReview.reply = this.replyModal.review.reply;
                }
                this.$message.success(this.$t("googlePlay.replySuccess"));
                this.replyModal.visible = false;
            } catch (err) {
                this.$message.error(this.$t("googlePlay.errorSendingReply"));
            } finally {
                this.replyModal.loading = false;
            }
        },
        onPageChange(page) {
            this.pagination.page = page;
            this.fetchReviews();
        },
        onPageSizeChange(current, size) {
            this.pagination.limit = size;
            this.pagination.page = 1;
            this.fetchReviews();
        },
        formatDate(date) {
            return moment(date).format("DD.MM.YYYY HH:mm");
        },
        truncateText(text, length) {
            if (!text) return "";
            if (text.length <= length) return text;
            return text.substring(0, length) + "...";
        },
    },
    watch: {
        "filters.status"() {
            this.pagination.page = 1;
            this.fetchReviews();
        },
        "filters.appId"() {
            this.pagination.page = 1;
            this.fetchReviews();
        },
        "filters.starRating"() {
            this.pagination.page = 1;
            this.fetchReviews();
        },
    },
    mounted() {
        this.fetchApps();
        this.fetchReviews();
    },
};
</script>

<style lang="scss" scoped>
.google-play-reviews-page {
    display: flex;
    gap: 20px;
    height: calc(100vh - 200px);
    padding: 16px;

    .reviews-panel {
        flex: 1;
        display: flex;
        flex-direction: column;
        background: var(--color-bg-default);
        border-radius: 12px;
        border: 1px solid var(--color-border-default);
        overflow: hidden;

        .panel-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 16px;
            border-bottom: 1px solid var(--color-border-default);
            flex-wrap: wrap;
            gap: 12px;

            .panel-title {
                display: flex;
                align-items: center;
                gap: 8px;
                font-size: 16px;
                font-weight: 600;
                margin: 0;

                .google-icon {
                    color: #fbbc05;
                    font-size: 20px;
                }

                .count-badge {
                    background: var(--color-bg-subtle);
                    padding: 2px 8px;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 500;
                }
            }

            .header-actions {
                display: flex;
                gap: 8px;
                align-items: center;
            }
        }

        .reviews-list {
            flex: 1;
            overflow-y: auto;
            padding: 8px;

            .review-item {
                display: flex;
                gap: 12px;
                padding: 12px;
                border-radius: 8px;
                cursor: pointer;
                transition: background 0.15s;
                border: 1px solid transparent;

                &:hover {
                    background: var(--color-bg-subtle);
                }

                &.selected {
                    background: var(--color-bg-subtle);
                    border-color: var(--color-border-strong);
                }

                .avatar-placeholder {
                    font-size: 36px;
                    color: var(--color-text-default);
                    flex-shrink: 0;
                }

                .review-main {
                    flex: 1;
                    min-width: 0;

                    .review-top {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        margin-bottom: 4px;

                        .author-name {
                            font-weight: 600;
                            font-size: 13px;
                        }

                        .review-date {
                            font-size: 11px;
                            color: var(--color-text-default);
                        }

                        .status-dot {
                            width: 8px;
                            height: 8px;
                            border-radius: 50%;
                            margin-left: auto;

                            &.new { background: #1890ff; }
                            &.replied { background: #52c41a; }
                            &.resolved { background: #8c8c8c; }
                        }
                    }

                    .star-rating {
                        margin-bottom: 6px;

                        .material-icons {
                            font-size: 14px;
                            color: #fbbc05;

                            &.empty {
                                color: #e0e0e0;
                            }
                        }
                    }

                    .review-text {
                        font-size: 13px;
                        line-height: 1.5;
                        margin: 0 0 6px;
                        color: var(--color-text-strong-default);

                        &.no-comment {
                            font-style: italic;
                            color: var(--color-text-default);
                        }
                    }

                    .review-bottom {
                        display: flex;
                        gap: 12px;
                        font-size: 11px;
                        color: var(--color-text-default);
                        align-items: center;

                        .app-link {
                            display: flex;
                            align-items: center;
                            gap: 4px;

                            .material-icons {
                                font-size: 14px;
                                color: #34a853;
                            }
                        }

                        .version-badge {
                            background: var(--color-bg-subtle);
                            padding: 2px 6px;
                            border-radius: 4px;
                            font-family: monospace;
                        }
                    }
                }

                .review-actions-mini {
                    opacity: 0;
                    transition: opacity 0.15s;
                }

                &:hover .review-actions-mini {
                    opacity: 1;
                }
            }

            .empty-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 60px 20px;
                color: var(--color-text-default);

                .material-icons {
                    font-size: 48px;
                    margin-bottom: 12px;
                    opacity: 0.5;
                }
            }
        }

        .loading-state {
            display: flex;
            justify-content: center;
            align-items: center;
            flex: 1;
        }

        .pagination-wrapper {
            padding: 12px 16px;
            border-top: 1px solid var(--color-border-default);
            display: flex;
            justify-content: center;
        }
    }

    .detail-panel {
        width: 380px;
        flex-shrink: 0;
        background: var(--color-bg-default);
        border-radius: 12px;
        border: 1px solid var(--color-border-default);
        display: flex;
        flex-direction: column;
        overflow: hidden;
        padding: 16px;

        &.empty {
            justify-content: center;
            align-items: center;
            color: var(--color-text-default);

            .material-icons {
                font-size: 48px;
                margin-bottom: 12px;
                opacity: 0.4;
            }

            p {
                font-size: 14px;
                margin: 0;
            }
        }

        .detail-header {
            display: flex;
            gap: 10px;
            margin-bottom: 12px;

            .material-icons {
                font-size: 40px;
                color: var(--color-text-default);
            }

            .author-details {
                flex: 1;

                .name {
                    display: block;
                    font-weight: 600;
                    font-size: 14px;
                }

                .date {
                    font-size: 12px;
                    color: var(--color-text-default);
                }
            }

            .badges {
                .status-badge {
                    padding: 2px 8px;
                    border-radius: 10px;
                    font-size: 10px;

                    &.new { background: #e6f7ff; color: #1890ff; }
                    &.replied { background: #f6ffed; color: #52c41a; }
                    &.resolved { background: #f5f5f5; color: #8c8c8c; }
                }
            }
        }

        .star-rating-large {
            margin-bottom: 12px;

            .material-icons {
                font-size: 24px;
                color: #fbbc05;

                &.empty {
                    color: #e0e0e0;
                }
            }
        }

        .detail-body {
            font-size: 14px;
            line-height: 1.6;
            margin-bottom: 16px;
            padding: 12px;
            background: var(--color-bg-subtle);
            border-radius: 8px;

            &.no-comment {
                font-style: italic;
                color: var(--color-text-default);
            }
        }

        .device-info {
            margin-bottom: 16px;
            padding: 12px;
            background: var(--color-bg-subtle);
            border-radius: 8px;

            .section-title {
                font-weight: 600;
                font-size: 12px;
                color: var(--color-text-default);
                margin-bottom: 8px;
            }

            .info-grid {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 8px;

                .info-item {
                    font-size: 12px;

                    .label {
                        color: var(--color-text-default);
                    }

                    .value {
                        font-weight: 500;
                    }
                }
            }
        }

        .reply-section {
            margin-bottom: 16px;
            padding: 12px;
            background: #f6ffed;
            border-radius: 8px;
            border-left: 3px solid #52c41a;

            .reply-header {
                display: flex;
                align-items: center;
                gap: 4px;
                font-weight: 600;
                font-size: 12px;
                color: #52c41a;
                margin-bottom: 8px;

                .material-icons {
                    font-size: 16px;
                }
            }

            .reply-content {
                font-size: 13px;
                line-height: 1.5;
            }

            .reply-date {
                font-size: 11px;
                color: var(--color-text-default);
                margin-top: 8px;
            }
        }

        .detail-actions {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
            margin-top: auto;
        }
    }

    .reply-preview {
        display: flex;
        gap: 12px;
        padding: 12px;
        background: var(--color-bg-subtle);
        border-radius: 8px;
        margin-bottom: 16px;

        .material-icons {
            font-size: 32px;
            color: var(--color-text-default);
        }

        .star-rating-small {
            .material-icons {
                font-size: 12px;
                color: #fbbc05;
            }
        }

        p {
            margin: 4px 0 0;
            font-size: 13px;
            color: var(--color-text-default);
        }
    }

    .char-count {
        text-align: right;
        font-size: 12px;
        color: var(--color-text-default);
        margin-top: 4px;
    }

    .status-indicator {
        display: inline-block;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        margin-right: 6px;

        &.new { background: #1890ff; }
        &.replied { background: #52c41a; }
        &.resolved { background: #8c8c8c; }
    }
}
</style>
